import { useState, useEffect } from 'react';
import { Flex, Button, Text, Modal, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ErrorModal, ModuleView, SuccessModal } from '@jadwalh/ui';
import instance from '../../../../../axios-instance';
import { SectionContainer } from './styles';
import { ReactComponent as PayTabs } from '../../../../../assets/images/svg/PayTabs.svg';
import { useSelector } from 'react-redux';
import { getProfileData } from '../../../../../store/profile.slice';
import { getProfile } from '../../../../../api/profile.api';
import { useFeatureGate } from '../../../../../hooks/useFeatureGate';
import { BlockedFeatureState } from '../../../../../components/BlockedFeatureState';

export default function PayTabsIntegration() {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const profile = useSelector(getProfileData);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showActivationForm, setShowActivationForm] = useState(false);
  const [inputApiKey, setInputApiKey] = useState('');
  const { isBlocked, hasAccess } = useFeatureGate('can_use_payments');
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await instance.get('app/integrations/paytabs/status');
        if (response.data.data?.is_connected) {
          setIsConnected(true);
          setApiKey(response.data.data.api_key);
        }
      } catch (error) {
        console.error('Error checking PayTabs status:', error);
      }
    };

    checkConnection();
  }, []);

  const handleActivate = () => {
    // Check if user has access to payments feature
    if (!hasAccess) {
      setShowBlockedModal(true);
      return;
    }

    // Check if user is member (role_value === 2)
    if (profile.role.role_value === 2) {
      setErrorMessage(formatMessage({ id: 'integrations.memberOnly' }));
      setShowErrorModal(true);
      return;
    }

    // Show activation form for admin/owner
    setShowActivationForm(true);
  };

  const handleSubmitApiKey = async () => {
    if (!inputApiKey.trim()) {
      setErrorMessage(formatMessage({ id: 'integrations.paytabs.apiKeyRequired' }));
      setShowErrorModal(true);
      return;
    }

    try {
      setIsLoading(true);
      const response = await instance.post('app/integrations/paytabs/activate', {
        api_key: inputApiKey.trim()
      });
      if (response.data.data) {
        setApiKey(inputApiKey.trim());
        setIsConnected(true);
        setShowActivationForm(false);
        setInputApiKey('');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error activating PayTabs:', error);
      setErrorMessage(formatMessage({ id: 'integrations.paytabs.error' }));
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirmation = () => {
    // Check if user is member (role_value === 2)
    if (profile.role.role_value === 2) {
      setErrorMessage(formatMessage({ id: 'integrations.memberOnly' }));
      setShowErrorModal(true);
      return;
    }
    setShowDeleteModal(true);
  };

  const handleDeleteIntegration = async () => {
    try {
      setIsLoading(true);
      await instance.post('app/integrations/paytabs/deactivate');
      setIsConnected(false);
      getProfile()
      setApiKey('');
      setShowDeleteModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error deactivating PayTabs:', error);
      setErrorMessage(formatMessage({ id: 'integrations.paytabs.error' }));
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SectionContainer>
      <ModuleView>
        <Flex
          direction={{ base: 'column' }}
          align={{ base: 'flex-start' }}
          className="detail_container"
        >
          <Flex justify="center" align="center" gap="10px">
            <PayTabs style={{ width: '20px', height: '20px' }} />
            <p className="SemiBold title">PayTabs</p>
          </Flex>
          <Flex direction={'column'} gap={'16px'} w={'100%'} pt={'16px'}>
            <Flex
              direction={'column'}
              align={'space-between'}
              justify={'space-between'}
              className="info"
            >
              <p>{formatMessage({ id: 'integrations.paytabs.description' })}</p>
              <br />
              <p>{formatMessage({ id: 'integrations.paytabs.benefits' })}</p>
              <Flex direction={'column'} gap={'0'}>
                <p style={{ marginBottom: '0' }}>
                  {formatMessage({ id: 'integrations.paytabs.benefit1.title' })}
                </p>
                <p>
                  {formatMessage({
                    id: 'integrations.paytabs.benefit1.description',
                  })}
                </p>
              </Flex>
              <Flex direction={'column'} gap={'0'} mt={'12px'}>
                <p style={{ marginBottom: '0' }}>
                  {formatMessage({ id: 'integrations.paytabs.benefit2.title' })}
                </p>
                <p>
                  {formatMessage({
                    id: 'integrations.paytabs.benefit2.description',
                  })}
                </p>
              </Flex>
              
              {/* Show API Key after successful integration */}
              {isConnected && apiKey && (
                <Flex direction={'column'} gap={'8px'} mt={'16px'} p={'12px'} style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ marginBottom: '0', fontWeight: '600' }}>
                    {formatMessage({ id: 'integrations.apiKey' })}
                  </p>
                  <Text
                    size="sm"
                    style={{
                      fontFamily: 'monospace',
                      backgroundColor: '#e9ecef',
                      padding: '8px',
                      borderRadius: '4px',
                      wordBreak: 'break-all'
                    }}
                  >
                    {apiKey}
                  </Text>
                </Flex>
              )}

              {/* Show API Key Input Form */}
              {showActivationForm && (
                <Flex direction={'column'} gap={'16px'} mt={'16px'} p={'16px'} style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <p style={{ marginBottom: '0', fontWeight: '600' }}>
                    {formatMessage({ id: 'integrations.paytabs.enterApiKey' })}
                  </p>
                  <TextInput
                    placeholder={formatMessage({ id: 'integrations.paytabs.apiKeyPlaceholder' })}
                    value={inputApiKey}
                    onChange={(e) => setInputApiKey(e.target.value)}
                    size="md"
                  />
                  <Flex gap="12px" justify="flex-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowActivationForm(false);
                        setInputApiKey('');
                      }}
                    >
                      {formatMessage({ id: 'cancel' })}
                    </Button>
                    <Button
                      onClick={handleSubmitApiKey}
                      loading={isLoading}
                      style={{ backgroundColor: '#242424' }}
                    >
                      {formatMessage({ id: 'integrations.submit' })}
                    </Button>
                  </Flex>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </ModuleView>
      
      {/* Hide main button when form is showing */}
      {!showActivationForm && (
        <Button 
          onClick={isConnected ? handleDeleteConfirmation : handleActivate}
          loading={isLoading}
          style={{
            backgroundColor: isConnected ? '#dc3545' : '#242424'
          }}
        >
          {isConnected 
            ? formatMessage({ id: 'integrations.deleteIntegration' })
            : formatMessage({ id: 'activation' })
          }
        </Button>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <ErrorModal
          showErrorModal={showErrorModal}
          setShowErrorModal={setShowErrorModal}
          message={errorMessage}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          showSuccessModal={showSuccessModal}
          setShowSuccessModal={setShowSuccessModal}
          message={formatMessage({ 
            id: isConnected ? 'integrations.paytabs.activationSuccess' : 'integrations.paytabs.deactivationSuccess' 
          })}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={formatMessage({ id: 'integrations.confirmDelete' })}
        centered
      >
        <Flex direction="column" gap="16px">
          <Text>
            {formatMessage({ id: 'integrations.deleteConfirmation' })}
          </Text>
          <Flex gap="12px" justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              {formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              onClick={handleDeleteIntegration}
              loading={isLoading}
              style={{ backgroundColor: '#dc3545' }}
            >
              {formatMessage({ id: 'yes' })}kjlkj
            </Button>
          </Flex>
        </Flex>
      </Modal>

      {/* Blocked Feature Modal */}
      {showBlockedModal && (
        <BlockedFeatureState
          title="استقبال المدفوعات غير متاح في باقتك الحالية"
          description="قم بالترقية للوصول إلى ميزة استقبال المدفوعات عبر PayTabs"
          onClose={() => setShowBlockedModal(false)}
        />
      )}
    </SectionContainer>
  );
}